import { useState, useEffect } from 'react';
import { Form, Card, Row, Col, Button, Image } from 'react-bootstrap';
import icQ from './assets/img/ic-q.svg';
import icA from './assets/img/ic-a.svg';
import { validateURL } from './utils/validation';
import icValidationError from './assets/img/ic-danger.svg';

const CountryWorkTax = ({ formData, handleChange, errors, isEditPage, handleWorkTaxSubmit }) => {
    const [isEditing, setIsEditing] = useState(!isEditPage); // ✅ If adding, fields are enabled
    const [tempFormData, setTempFormData] = useState({ ...formData }); // ✅ Store initial values// ✅ Enable editing mode

    const [localErrors, setLocalErrors] = useState({ doubleTaxationAgreementUrl: '' });
    const [touched, setTouched] = useState({ doubleTaxationAgreementUrl: false });
    const [dtaUrlInput, setDtaUrlInput] = useState(formData.doubleTaxationAgreementUrl || '');

    useEffect(() => {
        setDtaUrlInput(formData.doubleTaxationAgreementUrl || '');
    }, [formData.doubleTaxationAgreementUrl]);

    const validateDtaUrl = (v) => {
        if (!String(v || '').trim()) return '';
        return validateURL(v) ? '' : 'Please enter a valid URL.';
    };

    // ✅ normalize for safe href rendering
    const normalizeUrl = (raw) => {
        const s = (raw || "").trim();
        if (!s) return null;
        const withScheme = /^[a-zA-Z][a-zA-Z0-9+\-.]*:/.test(s) ? s : `https://${s}`;
        try {
            const u = new URL(withScheme);
            if (!["http:", "https:"].includes(u.protocol)) return null;
            return u.toString();
        } catch {
            return null;
        }
    };

    const prettyUrl = (u) => {
        try {
            const x = new URL(u);
            return x.hostname.replace(/^www\./, '');
        } catch {
            return 'Open link';
        }
    };

    const safeDtaHref = normalizeUrl(formData.doubleTaxationAgreementUrl || dtaUrlInput);
    const dtaLinkLabel = safeDtaHref ? prettyUrl(safeDtaHref) : '';


    const handleEditClick = () => {
        setIsEditing(true);
        setTempFormData({ ...formData }); // ✅ Store previous values
    };
    
    // ✅ Save changes and disable editing
    const handleSaveClick = async (e) => {
        e.preventDefault();
        if (typeof handleWorkTaxSubmit === 'function') { // ✅ Ensure it's a function before calling
            await handleWorkTaxSubmit(e);
            setIsEditing(false); // ✅ Exit edit mode after saving
        } else {
            console.error('Error: handleWorkTaxSubmit is not a function.');
        }
    };
    
    // ✅ Cancel editing and restore previous values
    const handleCancelClick = () => {
        setIsEditing(false);
        Object.keys(tempFormData).forEach((key) => {
            handleChange({ target: { name: key, value: tempFormData[key] } });
        });
    };

    return (
        <>
            <Card className="mb-4">
                <Card.Body>
                    {/* Title and Edit Button */}
                    <Row className="align-items-center">
                        <Col>
                            {isEditPage && (
                                <div className="float-end">
                                    {!isEditing ? (
                                        <Button variant="outline-secondary" className="btn-sm" onClick={handleEditClick}>Edit</Button>
                                    ) : (
                                        <>
                                            <Button variant="outline-secondary" className="btn-sm me-2" onClick={handleCancelClick}>Cancel</Button>
                                            <Button variant="primary" className="btn-sm" onClick={handleSaveClick}>Save</Button>
                                        </>
                                    )}
                                </div>
                            )}
                            <h3><span className="item-number">3. </span>Work & Tax</h3>
                            <hr />
                        </Col>
                    </Row>
                    {isEditing ? (
                        <>
                            <Row className="mb-3">
                                <Col md={6}> 
                                    {/* Regular week working hours */}
                                    <Form.Group controlId="weekWorkingHours">
                                        <Form.Label>How many hours per week are considered regular working hours? (regular is not the legal maximum)</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="weekWorkingHours"
                                            value={formData.weekWorkingHours || ''} // ✅ Ensure fallback value
                                            onChange={handleChange}
                                            min="0" // ✅ Ensure no negative values
                                            max="168" // ✅ Logical max (168 hours in a week)
                                            disabled={!isEditing} // ✅ Disable when not editing
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}> 
                                    {/* Minimum Wage */}
                                    <Form.Group controlId="minimumWage">
                                        <Form.Label>Is there a statutory minimum wage and, if so, how high is it?</Form.Label>
                                        <Form.Check
                                            type="radio"
                                            id="minimumWageYes"
                                            label="Yes"
                                            name="minimumWage"
                                            value="true" // ✅ send boolean-like string
                                            checked={formData.minimumWage === true} // ✅ compare to boolean
                                            onChange={(e) =>
                                                handleChange({
                                                    target: { name: 'minimumWage', value: e.target.value === 'true' } // ✅ coerce to boolean
                                                })
                                            }
                                            disabled={!isEditing} // ✅ Disable when not editing
                                        />
                                        <Form.Check
                                            type="radio"
                                            id="minimumWageNo"
                                            label="No"
                                            name="minimumWage"
                                            value="false" // ✅
                                            checked={formData.minimumWage === false} // ✅
                                            onChange={(e) =>
                                                handleChange({
                                                    target: { name: 'minimumWage', value: e.target.value === 'true' }, // ✅
                                                })
                                            }
                                            disabled={!isEditing}
                                        />
                                    </Form.Group>

                                    {/* ✅ Wage Amount Input Field (Only Show if 'Yes' is Selected) */}
                                    {formData.minimumWage && (
                                        <Form.Group controlId="wageAmount" className="mt-2">
                                            <Form.Label>Minimum Wage Amount (in local currency)</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="wageAmount"
                                                value={formData.wageAmount || ''} // ✅ Ensure fallback value
                                                onChange={handleChange}
                                                min="0" // ✅ Prevent negative values
                                                step="0.01" // ✅ Allow decimal values
                                                disabled={!isEditing} // ✅ Disable when not editing
                                            />
                                        </Form.Group>
                                    )}
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}> 
                                    {/* Internships regulation */}
                                    <Form.Group controlId="internshipsRegulation">
                                        <Form.Label>How is the payment of internships regulated by law?</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="internshipsRegulation"
                                            value={formData.internshipsRegulation || ''} // ✅ Ensure fallback value
                                            onChange={handleChange}
                                            disabled={!isEditing} // ✅ Disable when not editing
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}> 
                                    {/* Income tax liability */}
                                    <Form.Group controlId="incomeTaxLiability">
                                        <Form.Label>What income tax liability exists for foreigners in the destination country?</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="incomeTaxLiability"
                                            value={formData.incomeTaxLiability || ''} // ✅ Ensure fallback value
                                            onChange={handleChange}
                                            disabled={!isEditing} // ✅ Disable when not editing
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}> 
                                    {/* Double taxation agreement */}
                                    <Form.Group className="validated" controlId="doubleTaxationAgreementUrl">
                                        <Form.Label>Which countries have double taxation agreements in place? (url)</Form.Label>

                                        {(() => {
                                            const errMsg = touched.doubleTaxationAgreementUrl
                                                ? localErrors?.doubleTaxationAgreementUrl
                                                : errors?.doubleTaxationAgreementUrl;

                                            const hasError = !!errMsg;

                                            return (
                                                <>
                                                    <Form.Control
                                                        type="text"
                                                        name="doubleTaxationAgreementUrl"
                                                        value={dtaUrlInput}
                                                        onChange={(e) => {
                                                            const val = e.target.value;

                                                            // ✅ touch immediately
                                                            setTouched(t => ({ ...t, doubleTaxationAgreementUrl: true }));

                                                            // local input (smooth typing)
                                                            setDtaUrlInput(val);

                                                            // ✅ validate live
                                                            const msg = validateDtaUrl(val);
                                                            setLocalErrors(er => ({ ...er, doubleTaxationAgreementUrl: msg }));

                                                            // ✅ update parent state (even if empty; only invalid shows error)
                                                            handleChange({ target: { name: 'doubleTaxationAgreementUrl', value: val } });
                                                        }}
                                                        onBlur={() => {
                                                            const msg = validateDtaUrl(dtaUrlInput);
                                                            setLocalErrors(er => ({ ...er, doubleTaxationAgreementUrl: msg }));
                                                        }}
                                                        inputMode="url"
                                                        placeholder="example.com"
                                                        disabled={!isEditing}
                                                        isInvalid={hasError}
                                                        aria-invalid={hasError}
                                                    />

                                                    {hasError && (
                                                        <div className="custom-error-icon-wrapper">
                                                            <img
                                                                src={icValidationError}
                                                                alt={errMsg}
                                                                className="custom-error-icon"
                                                            />
                                                            <div className="custom-tooltip">
                                                                {errMsg}
                                                            </div>
                                                        </div>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </Form.Group>
                                </Col>
                                <Col md={6}> 
                                    {/* Health insurance compulsory */}
                                    <Form.Group controlId="healthInsurance">
                                        <Form.Label>Is health insurance compulsory?</Form.Label>
                                        <Form.Check
                                            type="radio"
                                            id="healthInsuranceYes"
                                            label="Yes"
                                            name="healthInsurance"
                                            value="true" // ✅
                                            checked={formData.healthInsurance === true} // ✅
                                            onChange={(e) =>
                                                handleChange({
                                                    target: { name: 'healthInsurance', value: e.target.value === 'true' }, // ✅
                                                })
                                            }
                                            disabled={!isEditing}
                                        />
                                        <Form.Check
                                            type="radio"
                                            id="healthInsuranceNo"
                                            label="No"
                                            name="healthInsurance"
                                            value="false" // ✅
                                            checked={formData.healthInsurance === false} // ✅
                                            onChange={(e) =>
                                                handleChange({
                                                    target: { name: 'healthInsurance', value: e.target.value === 'true' }, // ✅
                                                })
                                            }
                                            disabled={!isEditing}
                                        />

                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}> 
                                    {/* Accident insurance */}
                                    <Form.Group controlId="accidentInsurance">
                                        <Form.Label>Is there an obligation to have accident insurance for employees?</Form.Label>
                                        <Form.Check
                                            type="radio"
                                            id="accidentInsuranceYes"
                                            label="Yes"
                                            name="accidentInsurance"
                                            value="true"
                                            checked={formData.accidentInsurance === true}
                                            onChange={(e) =>
                                                handleChange({
                                                    target: { name: 'accidentInsurance', value: e.target.value === 'true' }
                                                })
                                            }
                                            disabled={!isEditing} // ✅ Disable when not editing
                                        />
                                        <Form.Check
                                            type="radio"
                                            id="accidentInsuranceNo"
                                            label="No"
                                            name="accidentInsurance"
                                            value="false"
                                            checked={formData.accidentInsurance === false}
                                            onChange={(e) =>
                                                handleChange({
                                                    target: { name: 'accidentInsurance', value: e.target.value === 'true' }
                                                })
                                            }
                                            disabled={!isEditing} // ✅ Disable when not editing
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </>
                    ) : (
                        <>  
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="How many hours per week are considered regular working hours? (regular is not the legal maximum)" />
                                                </span>
                                                <span className="text">
                                                    How many hours per week are considered regular working hours? (regular is not the legal maximum)
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="How many hours per week are considered regular working hours? (regular is not the legal maximum)" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>
                                                                {formData.weekWorkingHours || "N/A"}                                                                
                                                            </strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="Is there a statutory minimum wage and, if so, how high is it?" />
                                                </span>
                                                <span className="text">
                                                    Is there a statutory minimum wage and, if so, how high is it?
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="Is there a statutory minimum wage and, if so, how high is it?" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>
                                                                {formData.minimumWage === true && `${formData.wageAmount ?? "N/A"}`}
                                                                {formData.minimumWage === false && " No minimum wage"}
                                                            </strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="How is the payment of internships regulated by law?" />
                                                </span>
                                                <span className="text">
                                                    How is the payment of internships regulated by law?  
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="How is the payment of internships regulated by law?" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>{formData.internshipsRegulation || "N/A"}</strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="What income tax liability exists for foreigners in the destination country?" />
                                                </span>
                                                <span className="text">
                                                    What income tax liability exists for foreigners in the destination country?  
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="What income tax liability exists for foreigners in the destination country?" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>
                                                                {formData.incomeTaxLiability
                                                                    ? formData.incomeTaxLiability
                                                                    : "N/A"
                                                                }
                                                            </strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="Which countries have double taxation agreements in place?" />
                                                </span>
                                                <span className="text">
                                                    Which countries have double taxation agreements in place?
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="Which countries have double taxation agreements in place?" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>
                                                                {safeDtaHref ? (
                                                                    <a href={safeDtaHref} target="_blank" rel="noopener noreferrer">
                                                                        {dtaLinkLabel}
                                                                    </a>
                                                                ) : (
                                                                    "N/A"
                                                                )}
                                                            </strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="Is health insurance compulsory?" />
                                                </span>
                                                <span className="text">
                                                    Is health insurance compulsory?  
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="Is health insurance compulsory?" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>
                                                                {formData.healthInsurance === true
                                                                    ? "Yes"
                                                                    : formData.healthInsurance === false
                                                                    ? "No"
                                                                    : "N/A"
                                                                }
                                                            </strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={6}>
                                    <Card>
                                        <Card.Body className="p-3">
                                            <div className="icon-text align-items-center">
                                                <span className="icon" aria-hidden="true">
                                                    <Image src={icQ} alt="Is there an obligation to have accident insurance for employees?" />
                                                </span>
                                                <span className="text">
                                                    Is there an obligation to have accident insurance for employees?  
                                                </span>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    <Row className="sub-card-row">
                                        <Col className="justify-content-start">
                                            <Card>
                                                <Card.Body>
                                                    <div className="icon-text align-items-center">
                                                        <span className="icon" aria-hidden="true">
                                                            <Image src={icA} alt="Is there an obligation to have accident insurance for employees?" />
                                                        </span>
                                                        <span className="text">
                                                            <strong>
                                                                {formData.accidentInsurance === true
                                                                    ? "Yes"
                                                                    : formData.accidentInsurance === false
                                                                    ? "No"
                                                                    : "N/A"
                                                                }
                                                            </strong>
                                                        </span>
                                                    </div>
                                                </Card.Body>
                                            </Card>
                                        </Col>
                                    </Row>
                                </Col>
                            </Row>
                        </>
                    )}
                </Card.Body>
            </Card>
        </>
    );
};

export default CountryWorkTax;
